/**
 * Conflict-type constants returned by `classifyDateConflict`.
 *
 * `SCHEDULE_CONFLICT` — the date overlaps a shift the professional already has scheduled.
 * `OVERLAPPING_SHIFTS` — the date overlaps another date in the same submission batch.
 * `UNAVAILABLE` — the professional has marked themselves unavailable on that date.
 */
export const SCHEDULE_CONFLICT = 'SCHEDULE_CONFLICT';
export const OVERLAPPING_SHIFTS = 'OVERLAPPING_SHIFTS';
export const UNAVAILABLE = 'UNAVAILABLE';

/**
 * Returns the conflict type that applies to a given ISO date based on the
 * three Sets produced by `computeBulkAssignOverlaps` for a single shifts group.
 *
 * Precedence when multiple conflict types apply:
 * `SCHEDULE_CONFLICT` > `OVERLAPPING_SHIFTS` > `UNAVAILABLE`. Blockers come
 * first; among blockers the harder-to-resolve conflict wins.
 *
 * @param {string} isoDate The date to classify, formatted `YYYY-MM-DD`.
 * @param {{
 *   overlappingDates?: Set<string>,
 *   internalOverlapDates?: Set<string>,
 *   unavailableDates?: Set<string>
 * }} [groupConflicts] The per-group conflict sets. Any of the three Sets may be
 *   absent or empty. If `groupConflicts` itself is null/undefined the helper
 *   returns `null`.
 * @returns {('SCHEDULE_CONFLICT'|'OVERLAPPING_SHIFTS'|'UNAVAILABLE'|null)} The
 *   matching conflict constant, or `null` if no Set contains the date.
 */
const classifyDateConflict = (isoDate, groupConflicts) => {
	if (!groupConflicts) {
		return null;
	}
	if (groupConflicts.overlappingDates && groupConflicts.overlappingDates.has(isoDate)) {
		return SCHEDULE_CONFLICT;
	}
	if (groupConflicts.internalOverlapDates && groupConflicts.internalOverlapDates.has(isoDate)) {
		return OVERLAPPING_SHIFTS;
	}
	if (groupConflicts.unavailableDates && groupConflicts.unavailableDates.has(isoDate)) {
		return UNAVAILABLE;
	}
	return null;
};

export default classifyDateConflict;
